import { Button, Input } from "@/components/ui";
import type { SettlementSummaryResponse } from "@/lib/data/requests/reports/reports-settlement-summary";
import {
  hasValidSettlementDates,
  type SettlementSummaryFilters,
} from "@/lib/data/requests/reports/reports-settlement-summary";
import { ReportTable } from "../shared/ReportTable";
import { ReportRoleDropdown } from "../shared/ReportRoleDropdown";
import { ClosureChart } from "./ClosureChart";
import {
  awaitingColumns,
  closedColumns,
  issueColumns,
  workloadColumns,
} from "./SettlementColumns";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { functionalUpdate } from "@tanstack/react-table";

type Props = {
  filters: SettlementSummaryFilters;
  report?: SettlementSummaryResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isPinning: boolean;
  onPinRole: (roleId: string | null) => void;
  onFilters: (value: SettlementSummaryFilters) => void;
  onReset: () => void;
  onRetry: () => void;
};
export function SettlementSummaryReport({
  filters,
  report,
  isLoading,
  isFetching,
  isError,
  isPinning,
  onPinRole,
  onFilters,
  onReset,
  onRetry,
}: Props) {
  const data = report?.data;
  const selectedRoleId = filters.roleId
    ? data?.roles.find((item) => item.id === filters.roleId)?.id
    : (data?.selectedRoleId ?? undefined);
  const invalidDates = !hasValidSettlementDates(filters);
  const total = data?.totalClosedMatters ?? 0;
  const update = (change: Partial<SettlementSummaryFilters>) => {
    onFilters({ ...filters, ...change, page: 1 });
  };
  return (
    <div className="space-y-6" aria-busy={isFetching}>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">Settlements</h1>
        <span className="text-muted-foreground min-h-4 text-xs">
          {isFetching ? "Updating report…" : ""}
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {(["startDate", "endDate"] as const).map((key) => (
          <div key={key} className="w-44 space-y-1.5">
            <label
              htmlFor={`settlements-${key}`}
              className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
            >
              {key === "startDate" ? "Start date" : "End date"}
            </label>
            <Input
              id={`settlements-${key}`}
              type="date"
              value={filters[key]}
              onChange={(e) => {
                if (e.target.value) update({ [key]: e.target.value });
              }}
            />
          </div>
        ))}
        <Button
          variant="outline"
          className="h-10 rounded-md px-3"
          onClick={onReset}
        >
          Reset filters
        </Button>
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {isFetching ? "Updating report" : "Report updated"}
      </span>
      {invalidDates && (
        <p role="alert" className="text-destructive text-sm">
          Choose an ordered date range of at most ten years.
        </p>
      )}
      {isError ? (
        <div role="alert" className="py-8 text-sm">
          The report could not be loaded.{" "}
          <button className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {data &&
            (!data.freshness.ready ||
              data.freshness.pendingMatters > 0 ||
              data.freshness.missingMatters > 0) && (
              <div
                role="status"
                className="border-b bg-amber-50 px-5 py-3 text-sm text-amber-950"
              >
                Reporting is updating. {data.freshness.missingMatters} matters
                await complete publication and {data.freshness.pendingMatters}{" "}
                have pending changes. These figures may be incomplete.
              </div>
            )}
          <div className="grid items-start gap-6 xl:grid-cols-2">
            <section>
              <h2 className="mb-4 text-2xl font-semibold tracking-tight">
                Settled with issues
              </h2>
              <ReportTable
                columns={issueColumns}
                rows={data?.teams ?? []}
                total={data?.totals}
                rowKey={(r) => r.teamId ?? "unassigned"}
                isLoading={isLoading}
                tableName={tanstackTableNames.reportsSettlementSummaryIssues}
              />
            </section>
            <section>
              <h2 className="mb-4 text-2xl font-semibold tracking-tight">
                Closed matters
              </h2>
              <ClosureChart points={data?.closures ?? []} />
            </section>
          </div>
          <section>
            <h2 className="mb-4 text-2xl font-semibold tracking-tight">
              Awaiting linked deposit
            </h2>
            <ReportTable
              columns={awaitingColumns}
              rows={data?.teams ?? []}
              total={data?.totals}
              rowKey={(r) => r.teamId ?? "unassigned"}
              isLoading={isLoading}
              tableName={tanstackTableNames.reportsSettlementSummaryAwaiting}
            />
          </section>
          {!!data?.roles.length && (
            <section>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <h2 className="text-2xl font-semibold tracking-tight">
                  Matters with liens
                </h2>
                <div className="w-64 space-y-1.5">
                  <label
                    htmlFor="settlements-role"
                    className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
                  >
                    Assigned role
                  </label>
                  <ReportRoleDropdown
                    id="settlements-role"
                    roles={data.roles}
                    selectedRoleId={selectedRoleId}
                    pinnedRoleId={data.pinnedRoleId}
                    canPin={Boolean(
                      data.canManagePinnedRole &&
                        data.pinnedRoleId !== undefined &&
                        data.firmDateModified !== undefined,
                    )}
                    isPinning={isPinning}
                    onSelect={(roleId) => {
                      update({ roleId });
                    }}
                    onPin={onPinRole}
                  />
                </div>
              </div>
              <ReportTable
                columns={workloadColumns}
                rows={data.workload}
                rowKey={(r) => r.userId}
                tableName={tanstackTableNames.reportsSettlementSummaryWorkload}
              />
            </section>
          )}
          <section>
            <h2 className="mb-4 text-2xl font-semibold tracking-tight">
              Closed matter overview
            </h2>
            <ReportTable
              columns={closedColumns}
              rows={data?.closedMatters ?? []}
              rowKey={(r) => r.matterId}
              getMatterId={(r) => r.matterId}
              isLoading={isLoading}
              noResultsMessage="No closed matters match these filters."
              serverPagination={{
                pagination: {
                  pageIndex: filters.page - 1,
                  pageSize: filters.pageSize,
                },
                onPaginationChange: (updater) => {
                  const pagination = functionalUpdate(updater, {
                    pageIndex: filters.page - 1,
                    pageSize: filters.pageSize,
                  });
                  onFilters({
                    ...filters,
                    page: pagination.pageIndex + 1,
                    pageSize: pagination.pageSize,
                  });
                },
                pageCount: Math.max(1, Math.ceil(total / filters.pageSize)),
                rowCount: total,
                getCanNextPage:
                  filters.page * filters.pageSize < total && !isFetching,
              }}
              tableName={tanstackTableNames.reportsSettlementSummaryClosed}
            />
          </section>
        </>
      )}
    </div>
  );
}
